import { useState, useEffect } from 'react';
import { getJobById, updateJob, deleteJob } from '@/lib/api/jobs';
import { JobForm } from './JobForm';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

type JobEditModalProps = {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

export const JobEditModal = ({ jobId, isOpen, onClose, onSaved }: JobEditModalProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [initialData, setInitialData] = useState<Record<string, unknown> | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && jobId) {
      loadJob();
    }
  }, [isOpen, jobId]);

  const loadJob = async () => {
    try {
      setLoading(true);
      const jobData = await getJobById(jobId);
      
      setInitialData({
        title: jobData.title,
        clientId: jobData.client_id,
        siteAddress: jobData.site_address,
        startDate: jobData.start_date,
        estimatedEndDate: jobData.end_date,
        projectManagerId: jobData.project_manager_id,
        pmSource: jobData.pm_source,
        projectManagerCustom: jobData.project_manager_custom,
        budget: jobData.budget,
        description: jobData.description,
        status: jobData.status,
      });
    } catch (error) {
      console.error('Failed to load job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: Record<string, unknown>) => {
    try {
      setSaving(true);
      setSaveError('');
      await updateJob(jobId, formData);
      onSaved?.();
      onClose();
    } catch {
      setSaveError('Failed to update job. Please try again.');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      setSaveError('');
      await deleteJob(jobId);
      onSaved?.();
      onClose();
    } catch (error: unknown) {
      const e = error as { response?: { data?: { error?: string } } };
      setSaveError(e?.response?.data?.error || 'Failed to delete job. Please try again.');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Edit Job
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Update job details
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving}
              className="p-2 hover:bg-red-50 text-red-600 rounded-lg disabled:opacity-50 hover:text-red-700"
              title="Delete job"
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {saveError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {saveError}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="mt-2 text-gray-600">Loading job...</p>
              </div>
            </div>
          ) : (
            <JobForm
              onSubmit={handleSubmit}
              onCancel={onClose}
              initialData={initialData ?? undefined}
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Job"
          message="Are you sure you want to delete this job? This action cannot be undone."
          confirmLabel={saving ? 'Deleting…' : 'Delete'}
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};
